import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, race, take, takeEvery } from 'typed-redux-saga';

import { uploadFailed, backgroundLowQuality } from 'src/components/alert/slice';
import { isMultiBackground, getSceneSize, getSelectedBackgroundId, getAllBackgrounds } from 'src/selectors/layers';
import { getRegionSetting } from 'src/selectors';
import { NORMAL } from 'src/config/constants';
import { addDocument } from 'src/pages/editor/components/documents/slice';
import { uploadImageAndGetUrl, getImageSize, toSafeUploadName, isPSD, dataURLtoFile } from 'src/util/upload';
import { logError } from 'src/loggingManager';
import { BackgroundType } from 'src/types/background';
import { LayerType } from 'src/import/psd/webworker/message';

import {
    uploadBackgrounds,
    removeBackground,
    uploadAndReplaceBackground,
    backgroundUploaded,
    replaceBackgroundFailed,
    selectBackground,
    acceptResize,
    declineResize,
    cancelResizeModalUpload,
    showResizeModal,
    updateBackgroundRemovalModal,
    updateBackgroundById,
    updateBackgroundRemovalSuccess,
    updateBackgroundRemovalFailed,
    updateAssetRemovalModal,
    updateAssetRemovalSuccess,
    updateAssetRemovalFailed,
    loadPSD,
    importPSDFinish,
    importPSDStart,
} from './slice';
import { getSelectedAssetRemoval, getSelectedBackgroundRemoval } from './selectors';
import { updateConfiguration } from '../SceneVariation/slice';
import getSceneVariation from '../SceneVariation/selectors/getSceneVariation';
import { getLayersPSD, getResolutionPSD } from 'src/import/psd/selectors';
import { getSdk } from 'src/sdk';

const BACKGROUND_MIN_WARNING_THRESHOLD = 700;
const BACKGROUND_WARNING_THRESHOLD = 2000;
const BACKGROUND_MAX_THRESHOLD = 6000;

type ResizeParams = {
    height: number;
    width: number;
};

type UploadedBackground = {
    file: File;
    id: string;
    isFirstUpload: boolean;
    isFromPSD?: ResizeParams;
};

function imageOverMaxDimension(height: number, width: number, maxDimension: number): boolean {
    return width > maxDimension || height > maxDimension;
}

function imageOverScene(height: number, width: number, scene: { width: number; height: number }): boolean {
    return scene.width > 0 && scene.height > 0 && (width !== scene.width || height !== scene.height);
}

function calculateResizedBackgroundDimensions(
    height: number,
    width: number,
    maxDimension: number,
): { height: number; width: number } {
    let newHeight = height;
    let newWidth = width;
    if (height > width && height > maxDimension) {
        newHeight = maxDimension;
        newWidth = Math.round((maxDimension / height) * width);
    } else if (width > height && width > maxDimension) {
        newWidth = maxDimension;
        newHeight = Math.round((maxDimension / width) * height);
    } else if (height === width && height > maxDimension) {
        newHeight = maxDimension;
        newWidth = maxDimension;
    }

    return { height: newHeight, width: newWidth };
}

function* checkImageSize(
    height: number,
    width: number,
    sceneSize: { width: number; height: number },
): Generator<any, [boolean | null, boolean | null], any> {
    let resizeImage = false;
    let resizeToScene = imageOverScene(height, width, sceneSize);

    if (imageOverMaxDimension(height, width, BACKGROUND_WARNING_THRESHOLD) || resizeToScene) {
        if (imageOverMaxDimension(height, width, BACKGROUND_MAX_THRESHOLD)) {
            return [true, resizeToScene];
        }
        yield* put(showResizeModal(resizeToScene ? 'scene' : 'large'));
        const { acceptedResize, declinedResize, cancel } = yield* race({
            acceptedResize: take(acceptResize),
            declinedResize: take(declineResize),
            cancel: take(cancelResizeModalUpload),
        });

        if (cancel) {
            return [null, null];
        }

        if (acceptedResize && !resizeToScene) {
            resizeImage = true;
        } else if (declinedResize) {
            resizeImage = false;
            resizeToScene = false;
        }
    } else if (width < BACKGROUND_MIN_WARNING_THRESHOLD || height < BACKGROUND_MIN_WARNING_THRESHOLD) {
        yield* put(backgroundLowQuality());
    }

    return [resizeImage, resizeToScene];
}

function* uploadBackground(
    file: File,
    id: string,
    _verifySize: boolean,
    isFromPSD?: ResizeParams,
): Generator<any, { width: number; height: number } | null, any> {
    let { height, width } = yield* call(getImageSize, file);
    const sceneSize = yield* select(getSceneSize);
    const psdResolution = isFromPSD;
    let url: string;
    let resizeImage: boolean | null = null;
    let resizeScene: boolean | null = null;

    if (!psdResolution) {
        const normalizedSceneSize = {
            width: sceneSize.width || 0,
            height: sceneSize.height || 0,
        };
        const [shouldResizeImage, shouldResizeToScene] = yield* checkImageSize(height, width, normalizedSceneSize);
        resizeImage = shouldResizeImage;
        resizeScene = shouldResizeToScene;

        if (resizeImage === null) {
            // "Cancel" was clicked
            return null;
        }
    }

    try {
        const region = yield* select(getRegionSetting);
        let resizeParams: Record<string, string | number> | undefined;

        if (resizeImage) {
            resizeParams = calculateResizedBackgroundDimensions(height, width, BACKGROUND_WARNING_THRESHOLD);
            height = resizeParams.height as number;
            width = resizeParams.width as number;
        }
        if (psdResolution || resizeScene) {
            resizeParams = {
                height: (sceneSize.height || 0) > 0 ? (sceneSize.height as number) : psdResolution!.height,
                width: (sceneSize.width || 0) > 0 ? (sceneSize.width as number) : psdResolution!.width,
            };
            width = resizeParams.width as number;
            height = resizeParams.height as number;
        }

        url = yield* call(uploadImageAndGetUrl, getSdk(), file, region, resizeParams);
    } catch (error) {
        yield* put(uploadFailed(`Background: ${error}`));
        throw error;
    }

    yield* put(
        backgroundUploaded({
            id,
            url,
            width,
            height,
            name: file.name,
            mode: NORMAL,
        }),
    );

    return { width, height };
}

function* addBackground({ file, id, isFirstUpload, isFromPSD }: UploadedBackground): Generator<any, void, any> {
    try {
        Object.defineProperty(file, 'name', {
            writable: true,
            value: toSafeUploadName(file.name),
        });
        if (isPSD(file)) {
            yield* put(removeBackground(id));
            yield* put(loadPSD(file));
        } else {
            const result = yield* uploadBackground(file, id, true, isFromPSD);
            if (result && isFirstUpload) {
                const { width, height } = result;
                yield* put(addDocument({ width, height, id: 'Document 1' }));
            }
        }
    } catch (error) {
        logError(
            `unable to add background, there was an error while uploading/adding: ${error} ${file.name} ${file.size} ${file.type} firstUpload?: ${!isFirstUpload}`,
        );
        yield* put(removeBackground(id));
    }
}

function* addBackgrounds({ payload }: PayloadAction<UploadedBackground[]>): Generator<any, void, any> {
    // TODO: Allow uploading more than one psd at a time
    yield* all(payload.map((background) => call(addBackground, background)));
}

function* replaceBackground({ payload }: PayloadAction<{ file: File; id: string }>): Generator<any, void, any> {
    const { file, id } = payload;
    const verifySize = yield* select(isMultiBackground);
    try {
        yield* uploadBackground(file, id, verifySize);
    } catch (error) {
        logError(
            `unable to replace background, there was an error while uploading/updating: ${error} ${file.name} ${file.size} ${file.type}`,
        );
        yield* put(replaceBackgroundFailed(id));
    }
}

function* updateBackground({ payload }: PayloadAction<string>): Generator<any, void, any> {
    const bkgId = yield* select(getSelectedBackgroundRemoval);
    try {
        yield* put(updateBackgroundById({ id: bkgId, url: payload }));
        yield* put(updateBackgroundRemovalSuccess());
    } catch (error) {
        logError(
            `unable to replace background, there was an error while uploading/updating: BackgroundID ${bkgId},  ${payload}`,
        );
        yield* put(updateBackgroundRemovalFailed());
    }
}

function fromLayerToFile(layer: LayerType, isFirstUpload: boolean): UploadedBackground {
    const file = dataURLtoFile(layer.imageUrl || '', layer.name ? `${layer.name}.jpeg` : 'layer.jpeg');
    return {
        id: layer.id,
        file: file || new File([], 'layer.jpeg', { type: 'image/jpeg' }),
        isFirstUpload,
    };
}

function* uploadPSDAssets(): Generator<any, void, any> {
    const layers = yield* select(getLayersPSD);
    const resolution = yield* select(getResolutionPSD);
    const backgrounds = yield* select(getAllBackgrounds);
    const isFirstUpload = backgrounds.length === 0;
    const files = layers.map((layer, index) => {
        const file = fromLayerToFile(layer, index === 0 ? isFirstUpload : false);
        (file as any).isFromPSD = resolution;
        return file;
    });
    yield* put(uploadBackgrounds(files as BackgroundType[]));
    yield* put(importPSDFinish());
}

function* updateAsset({ payload }: PayloadAction<string>): Generator<any, void, any> {
    const bkgId = yield* select(getSelectedAssetRemoval);
    const data = bkgId?.split(';') || [];
    const sceneConfig = yield* select(getSceneVariation);
    if (data.length === 3 && sceneConfig.productConfigurations.length > 0) {
        const variableIndex = sceneConfig.productConfigurations.findIndex(
            (item) => item.variableOption === data[0] && item.variableAttribute === data[1],
        );
        const newConfig = sceneConfig.productConfigurations[variableIndex];

        try {
            const currentValue = newConfig.values[data[2]] as any;
            yield* put(
                updateConfiguration({
                    key: 'productConfigurations',
                    newConfiguration: {
                        ...newConfig,
                        values: {
                            ...newConfig.values,
                            [data[2]]: {
                                ...currentValue,
                                value: {
                                    ...(currentValue.value || {}),
                                    imageUrl: payload,
                                },
                            },
                        },
                    },
                    index: variableIndex,
                }),
            );
            // update scene variation with new url
            yield* put(updateAssetRemovalSuccess());
        } catch (error) {
            logError(
                `unable to replace background, there was an error while uploading/updating: BackgroundID ${bkgId},  ${payload}`,
            );
            yield* put(updateAssetRemovalFailed());
        }
    }
}

/**
 * This exists solely to select a new background if the currently selected background is deleted
 */
function* selectNewBackgroundIfDeleted({ payload }: PayloadAction<string>): Generator<any, void, any> {
    const selectedId = yield* select(getSelectedBackgroundId);
    if (payload === selectedId) {
        const backgrounds = yield* select(getAllBackgrounds);
        const firstBackground = backgrounds[0];
        if (firstBackground) {
            yield* put(selectBackground(firstBackground.id));
        }
    }
}

export default function* backgroundsSaga(): Generator<any, void, any> {
    yield* all([
        yield* takeEvery(uploadBackgrounds.type, addBackgrounds),
        yield* takeEvery(uploadAndReplaceBackground.type, replaceBackground),
        yield* takeEvery(removeBackground.type, selectNewBackgroundIfDeleted),
        yield* takeEvery(updateBackgroundRemovalModal.type, updateBackground),
        yield* takeEvery(updateAssetRemovalModal.type, updateAsset),
        yield* takeEvery(importPSDStart.type, uploadPSDAssets),
    ]);
}
